import {
	DateField,
	Drawer,
	DrawerBody,
	DrawerFooter,
	DrawerFooterActions,
	DrawerHeader,
	DrawerHeading,
	FormDrawer,
	FormDrawerContent,
	FormFetchDrawer,
	Id,
	MultiSelectField,
	TextField,
	usePermissionsQuery,
} from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PermissionName } from "../../../shared/types/permission";
import { accessTokenService } from "../shared/api/accessTokenService";
import { AccessTokenFormValues, AccessTokenPermissionsFormValues } from "../shared/types/accessToken";

interface CreateProps {
	open: boolean;
	onClose: () => void;
	onClosed?: () => void;
	onSubmit: (values: AccessTokenFormValues) => Promise<boolean>;
}

const initialValues: AccessTokenFormValues = { name: "", expiresAt: null };

export const AccessTokenCreateDrawer: FC<CreateProps> = (props) => {
	const { t } = useTranslation();

	return (
		<FormDrawer
			{...props}
			title={t("Create access token")}
			initialValues={initialValues}
			validate={(values) => {
				const errors: Partial<Record<keyof AccessTokenFormValues, string>> = {};
				if (!values.name) errors.name = t("Required field");
				return errors;
			}}
			renderForm={(formProps) => (
				<FormDrawerContent {...formProps}>
					<TextField name="name" label={t("Name")} required={true} />
					<DateField name="expiresAt" label={t("Expiry date")} />
				</FormDrawerContent>
			)}
		/>
	);
};

interface PermissionsProps {
	open: boolean;
	onClose: () => void;
	onClosed?: () => void;
	id: Id;
	onSubmit: (values: AccessTokenPermissionsFormValues) => Promise<boolean>;
}

export const AccessTokenPermissionsDrawer: FC<PermissionsProps> = (props) => {
	const { t } = useTranslation();
	const { data: allPermissions } = usePermissionsQuery<PermissionName>();

	return (
		<FormFetchDrawer
			{...props}
			title={t("Edit permissions")}
			queryKey={accessTokenService.showKey(props.id)}
			queryFn={() =>
				accessTokenService.show(props.id).then((r) => ({
					permissions: r.data.data.permissions,
				}))
			}
			renderSuccess={(formProps) => (
				<FormDrawerContent {...formProps}>
					<MultiSelectField<PermissionName, PermissionName>
						name="permissions"
						label={t("Permissions")}
						options={allPermissions.map((p) => p.name)}
						getLabel={(p) => p}
					/>
				</FormDrawerContent>
			)}
		/>
	);
};

const TokenInput = styled.input`
	width: 100%;
	font-family: monospace;
	font-size: 0.8rem;
	padding: 0.5rem;
	border: 1px solid #ccc;
	border-radius: 4px;
	background: #f5f5f5;
	word-break: break-all;
`;

const TokenLabel = styled.label`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	font-weight: 600;
	font-size: 0.875rem;
`;

const TokenHint = styled.p`
	margin-bottom: 1rem;
	font-size: 0.875rem;
`;

interface TokenCreatedProps {
	open: boolean;
	onClose: () => void;
	token: string;
}

export const AccessTokenCreatedDrawer: FC<TokenCreatedProps> = ({ open, onClose, token }) => {
	const { t } = useTranslation();

	return (
		<Drawer open={open} onClose={onClose}>
			<DrawerHeader onClose={onClose}>
				<DrawerHeading>{t("Token created")}</DrawerHeading>
			</DrawerHeader>
			<DrawerBody>
				<TokenHint>{t("Copy this token now. It will not be shown again.")}</TokenHint>
				<TokenLabel>
					{t("Token")}
					<TokenInput type="text" readOnly value={token} onClick={(e) => e.currentTarget.select()} />
				</TokenLabel>
			</DrawerBody>
			<DrawerFooter>
				<DrawerFooterActions />
			</DrawerFooter>
		</Drawer>
	);
};
